import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Clock3 } from 'lucide-react';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

export const CompOffValidity = () => {
  const [credits, setCredits] = useState([]);
  const [coBalance, setCoBalance] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('hrms-token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    (async () => {
      try {
        const [cRes, bRes] = await Promise.all([
          fetch(`${API_URL}/api/comp-off/credits`, { headers, credentials: 'include' }),
          fetch(`${API_URL}/api/me/leave-balance`, { headers, credentials: 'include' }),
        ]);
        const cs = await safeJson(cRes);
        if (cRes.ok && Array.isArray(cs)) setCredits(cs);
        const bs = await safeJson(bRes);
        if (bRes.ok && Array.isArray(bs)) {
          const co = bs.find(b => (b.leave_type || '').toLowerCase().includes('compensatory'));
          if (co) setCoBalance(co);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const hasBalance = coBalance && Number(coBalance.balance || 0) > 0;
  if (!hasBalance && credits.length === 0) return null;

  return (
    <Card className="border-amber-200" data-testid="comp-off-validity-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock3 className="w-4 h-4 text-amber-500" />
          Your Comp-Off Leaves — Expiry Dates
          {hasBalance && <span className="ml-auto text-xs font-normal text-slate-500">Balance: <strong>{coBalance.balance}</strong> day(s)</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {credits.length === 0 ? (
          <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5" data-testid="comp-off-no-dates">
            Your comp-off balance has no worked-date on record, so no expiry can be shown. Ask HR to re-credit it with the holiday date you worked.
          </p>
        ) : (
          <div className="space-y-1">
            {credits.slice(0, 6).map(c => {
              const noExpiry = (c.expires_at || '').startsWith('9999');
              const daysLeft = Math.ceil((new Date(c.expires_at) - Date.now()) / 86400000);
              const expired = !noExpiry && (c.is_expired || c.expired || daysLeft < 0);
              const used = Number(c.used_days || 0);
              const left = c.remaining_days != null ? Number(c.remaining_days) : Number(c.days || 0);
              const fullyUsed = left <= 0;
              return (
                <div key={c.credit_id}
                  className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded ${
                    fullyUsed ? 'bg-blue-50 text-blue-700'
                      : expired ? 'bg-slate-100 text-slate-400'
                      : daysLeft <= 3 ? 'bg-red-50' : 'bg-amber-50'}`}
                  data-testid={`co-credit-${c.credit_id}`}>
                  <span className={expired && !fullyUsed ? 'line-through' : ''}>
                    {c.worked_on ? <>Worked on <strong>{c.worked_on}</strong></> : <>Credited <strong>{c.credited_on}</strong></>} · {c.days} day{c.days !== 1 ? 's' : ''}
                    {used > 0 && <span className="text-blue-700"> · {used} taken</span>}
                    {!fullyUsed && !expired && used > 0 && <span className="text-emerald-700"> · {left} left</span>}
                  </span>
                  <span className={`font-semibold ${fullyUsed ? 'text-blue-700' : expired ? '' : daysLeft <= 3 ? 'text-red-600' : 'text-amber-700'}`}>
                    {fullyUsed ? 'Used' : expired ? 'Lapsed' : noExpiry ? 'No expiry' : `Expires ${c.expires_at}${daysLeft <= 5 ? ` — ${daysLeft} day(s) left!` : ''}`}
                  </span>
                </div>
              );
            })}
            <p className="text-[10px] text-slate-400 pt-1">Comp-off is valid for 1 month from the day you worked. Apply before the expiry date or the balance lapses.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompOffValidity;
